import type {SketchRegionFaceLoop, SketchRegionPlane, SketchRegionVec2, SketchRegionVec3} from './SketchRegionTypes';
import {
    dotVec3,
    liftPointFromPlane,
    pointsAlmostEqual3,
    projectPointToPlane,
    subtractVec2,
    subtractVec3,
} from './SketchRegionMath';

type CurveEntity = {
    entityId: string;
    entityType: string;
    geometry: Record<string, unknown>;
};

type BoundaryRun = {
    entity: CurveEntity;
    edges: SketchRegionFaceLoop['edges'];
    exactStart?: SketchRegionVec3;
    exactEnd?: SketchRegionVec3;
};

type AnalyticCurve =
    | {kind: 'line'; start: SketchRegionVec3; end: SketchRegionVec3}
    | {kind: 'circle'; center: SketchRegionVec3; radius: number};

const INTERSECTION_EPSILON = 1e-9;
const SPLINE_ENTITY_CLOSURE_EPSILON = 1e-5;

/**
 * 读取三维点。
 * @param value 原始值。
 * @returns 三维点。
 */
function asVec3(value: unknown): SketchRegionVec3 | null {
    if (!Array.isArray(value) || value.length < 3) {
        return null;
    }
    const [x, y, z] = value.map(Number);
    return Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z) ? [x, y, z] : null;
}

/**
 * 读取三维点数组。
 * @param value 原始值。
 * @returns 三维点数组。
 */
function asVec3Array(value: unknown): SketchRegionVec3[] | null {
    if (!Array.isArray(value)) {
        return null;
    }
    const points = value
        .map((item) => asVec3(item))
        .filter((item): item is SketchRegionVec3 => item !== null);
    return points.length === value.length && points.length > 0 ? points : null;
}

/**
 * 读取有限数字。
 * @param value 原始值。
 * @returns 数字。
 */
function asNumber(value: unknown): number | null {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
}

/**
 * 读取可选有限数字。
 * @param value 原始值。
 * @returns 数字；空值返回 null。
 */
function asOptionalNumber(value: unknown): number | null {
    if (value === null || value === undefined) {
        return null;
    }
    return asNumber(value);
}

/**
 * 读取字符串。
 * @param value 原始值。
 * @returns 字符串。
 */
function asString(value: unknown): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * 读取有限数字数组。
 * @param value 原始值。
 * @returns 数字数组。
 */
function asNumberArray(value: unknown): number[] | null {
    if (!Array.isArray(value)) {
        return null;
    }
    const numbers = value.map(Number);
    return numbers.every((item) => Number.isFinite(item)) ? numbers : null;
}

/**
 * 读取布尔值。
 * @param value 原始值。
 * @returns 布尔值。
 */
function asBoolean(value: unknown): boolean | null {
    return typeof value === 'boolean' ? value : null;
}

/**
 * 对字符串数组去重。
 * @param values 原始字符串数组。
 * @returns 去重后的字符串数组。
 */
function uniqueStrings(values: string[]): string[] {
    return Array.from(new Set(values.filter((value) => value.length > 0)));
}

/**
 * 克隆三维点。
 * @param point 原始点。
 * @returns 克隆点。
 */
function clonePoint(point: SketchRegionVec3): [number, number, number] {
    return [point[0], point[1], point[2]];
}

/**
 * 确保样条采样点保留闭合尾点。
 * @param points 原始采样点。
 * @returns 带闭合尾点的采样点。
 */
function closeCurvePoints(points: SketchRegionVec3[]): SketchRegionVec3[] {
    if (points.length <= 0) {
        return [];
    }
    const closedPoints = points.map(clonePoint);
    if (!pointsAlmostEqual3(closedPoints[0]!, closedPoints[closedPoints.length - 1]!)) {
        closedPoints.push(clonePoint(closedPoints[0]!));
    }
    return closedPoints;
}

/**
 * 归一化三维向量。
 * @param value 原始向量。
 * @param fallback 退化时的回退向量。
 * @returns 单位向量。
 */
function normalizeVec3(value: SketchRegionVec3, fallback: SketchRegionVec3): SketchRegionVec3 {
    const length = Math.hypot(value[0], value[1], value[2]);
    return length > 1e-9
        ? [value[0] / length, value[1] / length, value[2] / length]
        : clonePoint(fallback);
}

/**
 * 解析曲线实体索引。
 * @param entities 草图实体。
 * @returns 实体索引。
 */
function buildEntityMap(entities: SketchRegionEntityPayload[]): Map<string, CurveEntity> {
    return new Map(entities.map((entity) => [entity.entityId, {
        entityId: entity.entityId,
        entityType: entity.entityType,
        geometry: entity.geometry ?? {},
    }]));
}

/**
 * 为有向边选择主实体。
 * @param entityIds 边上的实体 ID。
 * @param entityMap 实体索引。
 * @returns 主实体。
 */
function resolvePrimaryEntity(
    entityIds: string[],
    entityMap: Map<string, CurveEntity>,
): CurveEntity | null {
    for (const entityId of entityIds) {
        const entity = entityMap.get(entityId);
        if (entity) {
            return entity;
        }
    }
    return null;
}

/**
 * 将面环有向边按连续实体分组。
 * @param loop 面环。
 * @param entityMap 实体索引。
 * @returns 边界连续段。
 */
function buildBoundaryRuns(
    loop: SketchRegionFaceLoop,
    entityMap: Map<string, CurveEntity>,
): BoundaryRun[] {
    const runs = loop.edges.reduce<BoundaryRun[]>((result, edge) => {
        const entity = resolvePrimaryEntity(edge.entityIds, entityMap);
        if (!entity) {
            return result;
        }
        const previous = result[result.length - 1];
        if (previous?.entity.entityId === entity.entityId) {
            previous.edges.push(edge);
            return result;
        }
        result.push({entity, edges: [edge]});
        return result;
    }, []);
    const first = runs[0];
    const last = runs[runs.length - 1];
    if (runs.length > 1 && first && last && first.entity.entityId === last.entity.entityId) {
        first.edges = [...last.edges, ...first.edges];
        runs.pop();
    }
    return runs;
}

/**
 * 解析曲线起点。
 * @param run 边界连续段。
 * @returns 起点。
 */
function resolveRunStart(run: BoundaryRun): SketchRegionVec3 {
    if (run.exactStart) {
        return clonePoint(run.exactStart);
    }
    return clonePoint(run.edges[0]!.start3);
}

/**
 * 解析曲线终点。
 * @param run 边界连续段。
 * @returns 终点。
 */
function resolveRunEnd(run: BoundaryRun): SketchRegionVec3 {
    if (run.exactEnd) {
        return clonePoint(run.exactEnd);
    }
    return clonePoint(run.edges[run.edges.length - 1]!.end3);
}

/**
 * 解析实体对应的解析曲线。
 * @param entity 草图实体。
 * @returns 解析曲线；暂不支持的实体返回 null。
 */
function resolveAnalyticCurve(entity: CurveEntity): AnalyticCurve | null {
    if (entity.entityType === 'line') {
        const start = asVec3(entity.geometry.startPoint ?? entity.geometry.start);
        const end = asVec3(entity.geometry.endPoint ?? entity.geometry.end);
        return start && end ? {kind: 'line', start, end} : null;
    }
    if (entity.entityType === 'circle' || entity.entityType === 'arc') {
        const center = asVec3(entity.geometry.center ?? entity.geometry.centerWorld);
        const radius = asNumber(entity.geometry.radius);
        return center && radius && radius > 0 ? {kind: 'circle', center, radius} : null;
    }
    return null;
}

/**
 * 二维点距离。
 * @param left 左点。
 * @param right 右点。
 * @returns 距离。
 */
function distance2(left: SketchRegionVec2, right: SketchRegionVec2): number {
    return Math.hypot(left[0] - right[0], left[1] - right[1]);
}

/**
 * 二维点加法。
 * @param left 左点。
 * @param right 右点。
 * @returns 结果。
 */
function addVec2(left: SketchRegionVec2, right: SketchRegionVec2): SketchRegionVec2 {
    return [left[0] + right[0], left[1] + right[1]];
}

/**
 * 二维点标量乘法。
 * @param point 输入点。
 * @param scalar 标量。
 * @returns 结果。
 */
function scaleVec2(point: SketchRegionVec2, scalar: number): SketchRegionVec2 {
    return [point[0] * scalar, point[1] * scalar];
}

/**
 * 解析圆圆交点。
 * @param left 左圆。
 * @param right 右圆。
 * @param plane 草图平面。
 * @returns 交点集合。
 */
function intersectCircleCircle(
    left: Extract<AnalyticCurve, {kind: 'circle'}>,
    right: Extract<AnalyticCurve, {kind: 'circle'}>,
    plane: SketchRegionPlane,
): SketchRegionVec3[] {
    const leftCenter = projectPointToPlane(left.center, plane);
    const rightCenter = projectPointToPlane(right.center, plane);
    const delta = subtractVec2(rightCenter, leftCenter);
    const distance = Math.hypot(delta[0], delta[1]);
    if (distance <= INTERSECTION_EPSILON) {
        return [];
    }
    const a = ((left.radius ** 2) - (right.radius ** 2) + (distance ** 2)) / (2 * distance);
    const hSquared = (left.radius ** 2) - (a ** 2);
    if (hSquared < -INTERSECTION_EPSILON) {
        return [];
    }
    const h = Math.sqrt(Math.max(0, hSquared));
    const unit: SketchRegionVec2 = [delta[0] / distance, delta[1] / distance];
    const base = addVec2(leftCenter, scaleVec2(unit, a));
    const perpendicular: SketchRegionVec2 = [-unit[1], unit[0]];
    return [
        addVec2(base, scaleVec2(perpendicular, h)),
        addVec2(base, scaleVec2(perpendicular, -h)),
    ].map((point) => liftPointFromPlane(point, plane));
}

/**
 * 解析线圆交点。
 * @param line 直线。
 * @param circle 圆。
 * @param plane 草图平面。
 * @returns 交点集合。
 */
function intersectLineCircle(
    line: Extract<AnalyticCurve, {kind: 'line'}>,
    circle: Extract<AnalyticCurve, {kind: 'circle'}>,
    plane: SketchRegionPlane,
): SketchRegionVec3[] {
    const start = projectPointToPlane(line.start, plane);
    const end = projectPointToPlane(line.end, plane);
    const center = projectPointToPlane(circle.center, plane);
    const direction = subtractVec2(end, start);
    const lengthSquared = direction[0] ** 2 + direction[1] ** 2;
    if (lengthSquared <= INTERSECTION_EPSILON) {
        return [];
    }
    const fromCenter = subtractVec2(start, center);
    const b = 2 * (direction[0] * fromCenter[0] + direction[1] * fromCenter[1]);
    const c = fromCenter[0] ** 2 + fromCenter[1] ** 2 - circle.radius ** 2;
    const discriminant = b ** 2 - 4 * lengthSquared * c;
    if (discriminant < -INTERSECTION_EPSILON) {
        return [];
    }
    const root = Math.sqrt(Math.max(0, discriminant));
    return [(-b - root) / (2 * lengthSquared), (-b + root) / (2 * lengthSquared)]
        .map((t) => addVec2(start, scaleVec2(direction, t)))
        .map((point) => liftPointFromPlane(point, plane));
}

/**
 * 解析两条直线交点。
 * @param left 左直线。
 * @param right 右直线。
 * @param plane 草图平面。
 * @returns 交点集合。
 */
function intersectLineLine(
    left: Extract<AnalyticCurve, {kind: 'line'}>,
    right: Extract<AnalyticCurve, {kind: 'line'}>,
    plane: SketchRegionPlane,
): SketchRegionVec3[] {
    const p = projectPointToPlane(left.start, plane);
    const r = subtractVec2(projectPointToPlane(left.end, plane), p);
    const q = projectPointToPlane(right.start, plane);
    const s = subtractVec2(projectPointToPlane(right.end, plane), q);
    const denominator = r[0] * s[1] - r[1] * s[0];
    if (Math.abs(denominator) <= INTERSECTION_EPSILON) {
        return [];
    }
    const qMinusP = subtractVec2(q, p);
    const t = (qMinusP[0] * s[1] - qMinusP[1] * s[0]) / denominator;
    return [liftPointFromPlane(addVec2(p, scaleVec2(r, t)), plane)];
}

/**
 * 解析两条解析曲线的交点。
 * @param left 左曲线。
 * @param right 右曲线。
 * @param plane 草图平面。
 * @returns 交点集合。
 */
function intersectAnalyticCurves(
    left: AnalyticCurve,
    right: AnalyticCurve,
    plane: SketchRegionPlane,
): SketchRegionVec3[] {
    if (left.kind === 'circle' && right.kind === 'circle') {
        return intersectCircleCircle(left, right, plane);
    }
    if (left.kind === 'line' && right.kind === 'circle') {
        return intersectLineCircle(left, right, plane);
    }
    if (left.kind === 'circle' && right.kind === 'line') {
        return intersectLineCircle(right, left, plane);
    }
    if (left.kind === 'line' && right.kind === 'line') {
        return intersectLineLine(left, right, plane);
    }
    return [];
}

/**
 * 选择距离采样交点最近的解析交点。
 * @param candidates 候选交点。
 * @param approximate 采样交点。
 * @param plane 草图平面。
 * @returns 最近的解析交点。
 */
function pickClosestIntersection(
    candidates: SketchRegionVec3[],
    approximate: SketchRegionVec3,
    plane: SketchRegionPlane,
): SketchRegionVec3 | null {
    const target = projectPointToPlane(approximate, plane);
    return candidates
        .map((point) => ({point, distance: distance2(projectPointToPlane(point, plane), target)}))
        .sort((left, right) => left.distance - right.distance)[0]?.point ?? null;
}

/**
 * 将连续段边界端点提升为解析曲线真实交点。
 * @param runs 边界连续段。
 * @param plane 草图平面。
 */
function snapRunBoundaryIntersections(runs: BoundaryRun[], plane: SketchRegionPlane): void {
    if (runs.length <= 1) {
        return;
    }
    runs.forEach((run, index) => {
        const next = runs[(index + 1) % runs.length]!;
        const leftCurve = resolveAnalyticCurve(run.entity);
        const rightCurve = resolveAnalyticCurve(next.entity);
        if (!leftCurve || !rightCurve) {
            return;
        }
        const approximate = run.edges[run.edges.length - 1]!.end3;
        const exact = pickClosestIntersection(
            intersectAnalyticCurves(leftCurve, rightCurve, plane),
            approximate,
            plane,
        );
        if (!exact) {
            return;
        }
        run.exactEnd = exact;
        next.exactStart = exact;
    });
}

/**
 * 判断边界连续段是否沿实体原始方向。
 * @param run 边界连续段。
 * @returns 是否沿原始方向。
 */
function isRunForward(run: BoundaryRun): boolean {
    const forwardCount = run.edges.filter((edge) => edge.forward).length;
    return forwardCount >= run.edges.length - forwardCount;
}

/**
 * 计算点在圆/椭圆局部坐标中的角度。
 * @param point 目标点。
 * @param center 曲线中心。
 * @param xAxis 曲线局部 X 轴。
 * @param yAxis 曲线局部 Y 轴。
 * @returns 参数角。
 */
function angleOfPoint(
    point: SketchRegionVec3,
    center: SketchRegionVec3,
    xAxis: SketchRegionVec3,
    yAxis: SketchRegionVec3,
): number {
    const relative = subtractVec3(point, center);
    return Math.atan2(dotVec3(relative, yAxis), dotVec3(relative, xAxis));
}

/**
 * 计算点在旋转椭圆基础曲线上的参数角。
 *
 * 说明：先投影到草图平面轴，再消除椭圆整体旋转并按长短半轴归一化，
 * 确保局部闭合区域恢复的参数与 `THREE.EllipseCurve` 定义一致。
 *
 * @param point 目标点。
 * @param center 椭圆中心。
 * @param xAxis 草图平面 X 轴。
 * @param yAxis 草图平面 Y 轴。
 * @param radiusX 长半轴。
 * @param radiusY 短半轴。
 * @param rotation 椭圆整体旋转角。
 * @returns 椭圆参数角。
 */
function ellipseParameterAngleOfPoint(
    point: SketchRegionVec3,
    center: SketchRegionVec3,
    xAxis: SketchRegionVec3,
    yAxis: SketchRegionVec3,
    radiusX: number,
    radiusY: number,
    rotation: number,
): number {
    const relative = subtractVec3(point, center);
    const planeX = dotVec3(relative, xAxis);
    const planeY = dotVec3(relative, yAxis);
    const cosRotation = Math.cos(rotation);
    const sinRotation = Math.sin(rotation);
    const ellipseX = planeX * cosRotation + planeY * sinRotation;
    const ellipseY = -planeX * sinRotation + planeY * cosRotation;
    return Math.atan2(ellipseY / radiusY, ellipseX / radiusX);
}

/**
 * 从线段连续段构建精确线段。
 * @param run 边界连续段。
 * @returns 线段曲线。
 */
function buildLineSegment(run: BoundaryRun): ProfileCurveSegment | null {
    const start = resolveRunStart(run);
    const end = resolveRunEnd(run);
    if (pointsAlmostEqual3(start, end)) {
        return null;
    }
    return {
        kind: 'line',
        start,
        end,
    };
}

/**
 * 从圆连续段构建圆或圆弧。
 * @param run 边界连续段。
 * @param normal 草图平面法向。
 * @param fallbackYAxis 草图平面 Y 轴。
 * @returns 圆曲线段。
 */
function buildCircleSegment(
    run: BoundaryRun,
    normal: SketchRegionVec3,
    fallbackYAxis: SketchRegionVec3,
): ProfileCurveSegment | null {
    const center = asVec3(run.entity.geometry.center ?? run.entity.geometry.centerWorld);
    const radius = asNumber(run.entity.geometry.radius);
    const xAxis = asVec3(run.entity.geometry.xAxis);
    if (!center || !radius || radius <= 0 || !xAxis) {
        return null;
    }
    const yAxis = asVec3(run.entity.geometry.yAxis) ?? fallbackYAxis;
    const start = resolveRunStart(run);
    const end = resolveRunEnd(run);
    if (pointsAlmostEqual3(start, end, 1e-5)) {
        return {
            kind: 'circle',
            center,
            radius,
            normal,
            xAxis,
        };
    }
    const forward = isRunForward(run);
    return {
        kind: 'arc',
        center,
        radius,
        startAngle: angleOfPoint(start, center, xAxis, yAxis),
        endAngle: angleOfPoint(end, center, xAxis, yAxis),
        clockwise: !forward,
        normal,
        xAxis,
    };
}

/**
 * 从圆弧连续段构建精确圆弧。
 * @param run 边界连续段。
 * @param normal 草图平面法向。
 * @param fallbackYAxis 草图平面 Y 轴。
 * @returns 圆弧曲线段。
 */
function buildArcSegment(
    run: BoundaryRun,
    normal: SketchRegionVec3,
    fallbackYAxis: SketchRegionVec3,
): ProfileCurveSegment | null {
    const center = asVec3(run.entity.geometry.center ?? run.entity.geometry.centerWorld);
    const radius = asNumber(run.entity.geometry.radius);
    const xAxis = asVec3(run.entity.geometry.xAxis);
    if (!center || !radius || radius <= 0 || !xAxis) {
        return null;
    }
    const yAxis = asVec3(run.entity.geometry.yAxis) ?? fallbackYAxis;
    const forward = isRunForward(run);
    return {
        kind: 'arc',
        center,
        radius,
        startAngle: angleOfPoint(resolveRunStart(run), center, xAxis, yAxis),
        endAngle: angleOfPoint(resolveRunEnd(run), center, xAxis, yAxis),
        clockwise: forward ? run.entity.geometry.clockwise === true : run.entity.geometry.clockwise !== true,
        normal,
        xAxis,
    };
}

/**
 * 从椭圆连续段构建精确椭圆或椭圆弧。
 * @param run 边界连续段。
 * @param normal 草图平面法向。
 * @returns 椭圆曲线段。
 */
function buildEllipseSegment(run: BoundaryRun, normal: SketchRegionVec3): ProfileCurveSegment | null {
    const center = asVec3(run.entity.geometry.center ?? run.entity.geometry.centerWorld);
    const radiusX = asNumber(run.entity.geometry.radiusX);
    const radiusY = asNumber(run.entity.geometry.radiusY);
    const xAxis = asVec3(run.entity.geometry.xAxis);
    const yAxis = asVec3(run.entity.geometry.yAxis);
    if (!center || !radiusX || !radiusY || radiusX <= 0 || radiusY <= 0 || !xAxis || !yAxis) {
        return null;
    }
    const start = resolveRunStart(run);
    const end = resolveRunEnd(run);
    const rotation = asNumber(run.entity.geometry.rotation) ?? 0;
    const isFullEllipse = pointsAlmostEqual3(start, end, 1e-5);
    const forward = isRunForward(run);
    return {
        kind: 'ellipse',
        center,
        radiusX,
        radiusY,
        rotation,
        startAngle: isFullEllipse
            ? (asNumber(run.entity.geometry.startAngle) ?? 0)
            : ellipseParameterAngleOfPoint(start, center, xAxis, yAxis, radiusX, radiusY, rotation),
        endAngle: isFullEllipse
            ? (asNumber(run.entity.geometry.endAngle) ?? Math.PI * 2)
            : ellipseParameterAngleOfPoint(end, center, xAxis, yAxis, radiusX, radiusY, rotation),
        clockwise: isFullEllipse
            ? run.entity.geometry.clockwise === true
            : (forward ? run.entity.geometry.clockwise === true : run.entity.geometry.clockwise !== true),
        normal,
        xAxis,
        yAxis,
    };
}

/**
 * 判断实体是否为样条类实体。
 * @param entityType 实体类型。
 * @returns 是否为样条类实体。
 */
function isSplineEntityType(entityType: string): boolean {
    return entityType === 'spline'
        || entityType === 'style_spline'
        || entityType === 'style-spline';
}

/**
 * 判断样条实体是否具备闭合语义。
 * @param entity 样条实体。
 * @returns 是否闭合。
 */
function isClosedSplineEntity(entity: CurveEntity): boolean {
    if (!isSplineEntityType(entity.entityType)) {
        return false;
    }
    if (entity.geometry.isClosed === true || entity.geometry.closureKind === 'closed') {
        return true;
    }
    const curvePoints = asVec3Array(entity.geometry.curvePoints)
        ?? asVec3Array(entity.geometry.points)
        ?? [];
    return curvePoints.length >= 2
        && pointsAlmostEqual3(curvePoints[0]!, curvePoints[curvePoints.length - 1]!, SPLINE_ENTITY_CLOSURE_EPSILON);
}

/**
 * 从完整闭合样条实体构建样条曲线段。
 *
 * @param entity 样条实体。
 * @returns 样条曲线段。
 */
function buildClosedSplineEntitySegment(entity: CurveEntity): ProfileCurveSegment | null {
    if (!isClosedSplineEntity(entity)) {
        return null;
    }
    const curvePoints = closeCurvePoints(
        asVec3Array(entity.geometry.curvePoints)
            ?? asVec3Array(entity.geometry.points)
            ?? [],
    );
    const controlPoints = asVec3Array(entity.geometry.controlPoints) ?? curvePoints;
    if (curvePoints.length < 3 || controlPoints.length < 3) {
        return null;
    }
    const curveKind = asString(entity.geometry.curveKind);
    const closureKind = asString(entity.geometry.closureKind);
    return {
        kind: 'spline',
        controlPoints: controlPoints.map(clonePoint),
        curvePoints,
        curveKind: curveKind === 'bezier' || curveKind === 'bspline' || curveKind === 'spline'
            ? curveKind
            : undefined,
        degree: asOptionalNumber(entity.geometry.degree),
        weights: asNumberArray(entity.geometry.weights) ?? undefined,
        isRational: asBoolean(entity.geometry.isRational) ?? undefined,
        closureKind: closureKind === 'open' || closureKind === 'closed' ? closureKind : undefined,
        isClosed: true,
    };
}

/**
 * 从完整闭合样条连续段构建样条曲线段。
 *
 * 说明：区域选择器会把闭合样条采样成多条图边，但这些图边仍属于同一个样条实体。
 * 只有当当前 run 首尾闭合时，才说明它覆盖完整自闭合样条；被其它草图实体切分的局部样条段
 * 继续回退为采样折线，避免把局部区域错误扩展成整条样条。
 *
 * @param run 边界连续段。
 * @returns 样条曲线段。
 */
function buildSplineSegment(run: BoundaryRun): ProfileCurveSegment | null {
    const isRunClosed = pointsAlmostEqual3(resolveRunStart(run), resolveRunEnd(run), SPLINE_ENTITY_CLOSURE_EPSILON);
    if (!isRunClosed) {
        return null;
    }
    return buildClosedSplineEntitySegment(run.entity);
}

/**
 * 当完整区域边界只来自一个闭合样条时，直接恢复完整样条曲线段。
 *
 * 设计说明：
 * 1. 区域解析会先把样条采样成折线图边，凹形或不规则样条在追踪时可能被拆成多个 run；
 * 2. 只要外环边界实体集合只有这个闭合样条，就说明当前区域仍然是完整样条轮廓；
 * 3. 存在开放 splitter 或其它边界实体时必须返回 null，避免把局部区域误扩成整条样条。
 *
 * @param loop 面环。
 * @param entityMap 实体索引。
 * @returns 完整样条曲线段。
 */
function buildSingleClosedSplineLoopSegment(
    loop: SketchRegionFaceLoop,
    entityMap: Map<string, CurveEntity>,
): ProfileCurveSegment | null {
    if (loop.splitterEntityIds.length > 0) {
        return null;
    }
    const boundaryEntityIds = uniqueStrings(loop.boundaryEntityIds);
    if (boundaryEntityIds.length !== 1) {
        return null;
    }
    const entity = entityMap.get(boundaryEntityIds[0]!);
    if (!entity || !isClosedSplineEntity(entity)) {
        return null;
    }
    return buildClosedSplineEntitySegment(entity);
}

/**
 * 从边界连续段构建精确曲线段。
 * @param run 边界连续段。
 * @param normal 草图平面法向。
 * @param yAxis 草图平面 Y 轴。
 * @returns 曲线段。
 */
function buildSegmentFromRun(
    run: BoundaryRun,
    normal: SketchRegionVec3,
    yAxis: SketchRegionVec3,
): ProfileCurveSegment | null {
    if (run.entity.entityType === 'line') {
        return buildLineSegment(run);
    }
    if (run.entity.entityType === 'circle') {
        return buildCircleSegment(run, normal, yAxis);
    }
    if (run.entity.entityType === 'arc') {
        return buildArcSegment(run, normal, yAxis);
    }
    if (run.entity.entityType === 'ellipse') {
        return buildEllipseSegment(run, normal);
    }
    if (isSplineEntityType(run.entity.entityType)) {
        return buildSplineSegment(run);
    }
    return null;
}

/**
 * 从闭合区域面环恢复精确曲线段。
 *
 * @param loop 面环。
 * @param entities 草图实体。
 * @param normal 草图平面法向。
 * @param yAxis 草图平面 Y 轴。
 * @returns 可用于 Puppy CAD 内核 wire 构造的曲线段；无法完整表达时返回空数组。
 */
export function resolveSketchRegionLoopCurveSegments(
    loop: SketchRegionFaceLoop,
    entities: SketchRegionEntityPayload[],
    plane: SketchRegionPlane,
): ProfileCurveSegment[] {
    const entityMap = buildEntityMap(entities);
    const singleClosedSplineSegment = buildSingleClosedSplineLoopSegment(loop, entityMap);
    if (singleClosedSplineSegment) {
        return [singleClosedSplineSegment];
    }

    const runs = buildBoundaryRuns(loop, entityMap);
    if (runs.length === 0) {
        return [];
    }
    snapRunBoundaryIntersections(runs, plane);
    const segments = runs
        .map((run) => buildSegmentFromRun(run, normalizeVec3(plane.normal, [0, 0, 1]), plane.yAxis))
        .filter((segment): segment is ProfileCurveSegment => segment !== null);
    return segments.length === runs.length ? segments : [];
}
